import { ApiError, normalizeApiError } from '../../services/apiError';

/**
 * The plan's next automatic credits: when (ISO, UTC), how many minutes away when the refusal was made (0 when
 * already due), Free-plan ("free") or paid-plan ("plan") credits, and whether it restarts the Free monthly cycle.
 */
export interface PlanningCreditNextGrant {
  amount: number;
  scheduled_at: string;
  minutes: number;
  kind?: 'free' | 'plan';
  cycle_reset?: boolean;
}

/** The 402 detail trigger-agents sends when a design run cannot be paid for (worded in the user's language). */
export interface PlanningCreditRefusalDetail {
  code: 'planning_out_of_credits';
  message: string;
  language?: string;
  add_credits?: boolean;
  upgrade_available?: boolean;
  balance?: number;
  required?: number;
  shortfall?: number;
  /** A design press can bill sections not planned yet: ``required`` is then a floor. */
  open_ended?: boolean;
  next_grant?: PlanningCreditNextGrant | null;
}

/** Thrown in place of a generic trigger failure so the start flow shows the refusal in chat, not a toast. */
export const PLANNING_CREDIT_REFUSED = 'PLANNING_CREDIT_REFUSED';

export const planningCreditRefusalFrom = (error: unknown): PlanningCreditRefusalDetail | null => {
  const normalized = normalizeApiError(error);
  const detail = normalized.detail as Partial<PlanningCreditRefusalDetail> | undefined;

  if (normalized.status !== 402 || !detail || detail.code !== 'planning_out_of_credits') return null;
  if (typeof detail.message !== 'string' || !detail.message.trim()) return null;

  return detail as PlanningCreditRefusalDetail;
};

export const isPlanningCreditRefused = (error: unknown): boolean => (
  error instanceof ApiError && error.code === PLANNING_CREDIT_REFUSED
);

/** Show a credit refusal through ``show`` and rethrow it as one the start flow does not toast. */
export const throwIfPlanningCreditRefused = (
  error: unknown, show: (refusal: PlanningCreditRefusalDetail) => void,
): void => {
  const refusal = planningCreditRefusalFrom(error);

  if (!refusal) return;
  show(refusal);
  throw new ApiError({ code: PLANNING_CREDIT_REFUSED, message: refusal.message, status: 402, detail: refusal });
};
